import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router";
import {
  ActionBar,
  DataDetail,
  DataList,
  LogLines,
  PageHeader,
  RelatedLinks,
  PlacementPicker,
  StatusPill,
  useActionRunner,
  type DataListColumn,
  ImageVersion,
  AppHealth,
} from "@ozpanel/kit";
import { Button, Section, StateView, formatPercent, useAuth, useToast } from "@ozpanel/ui";
import { useAccountsApi, type AppNodeView, type Backend } from "../../accountsApi";
import { useQuery as useQ } from "@tanstack/react-query";
import { appActions } from "./appActions";

/** The two caps an app can be given, and the only safe way to change them.
 *
 * PUT /apps/{name} is a WHOLE-OBJECT replace — CLAUDE.md records this under "Moving an app between
 * nodes": sending a partial body is refused or silently drops what it omits, which is why the
 * placement endpoint exists separately. So this reads the app it was given and sends every field
 * back, changing only the two here. scratch_mb had no console anywhere for the same reason: there
 * is no partial-update route, and nothing was willing to re-send the whole object.
 *
 * Zero is not "no limit" for either — it means "inherit", the tenant slice's ceiling for memory
 * and the plan's default for scratch — so the placeholder says so rather than showing 0. */
function ResourceLimits({ app, onSaved }: { app: Backend; onSaved: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [mem, setMem] = React.useState(String(app.mem_max_mb || ""));
  const [scratch, setScratch] = React.useState(String(app.scratch_mb || ""));
  const [busy, setBusy] = React.useState(false);
  const dirty =
    mem !== String(app.mem_max_mb || "") || scratch !== String(app.scratch_mb || "");

  const save = async () => {
    setBusy(true);
    try {
      await client.apiFetch(`/apps/${encodeURIComponent(app.name)}`, {
        method: "PUT",
        body: JSON.stringify({ ...app, mem_max_mb: Number(mem) || 0, scratch_mb: Number(scratch) || 0 }),
      });
      notify({ kind: "success", title: "Limits applied" });
      onSaved();
    } catch (e) {
      notify({ kind: "error", title: "Could not apply them", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Resource limits" className="mt-8">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[12px] text-muted">
          <span>Memory (MB)</span>
          <input
            value={mem}
            placeholder="inherit the account's ceiling"
            onChange={(e) => setMem(e.target.value)}
            className="w-56 rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg"
          />
        </label>
        <label className="flex flex-col gap-1 text-[12px] text-muted">
          <span>Scratch space (MB)</span>
          <input
            value={scratch}
            placeholder="the plan's default"
            onChange={(e) => setScratch(e.target.value)}
            className="w-56 rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg"
          />
        </label>
        <Button size="sm" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? "Applying…" : "Apply"}
        </Button>
      </div>
      <p className="mt-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">
        Applying regenerates the unit and restarts the app, so it is briefly unavailable. Leaving a
        box empty means inherit — memory falls back to the account&apos;s ceiling and scratch to the
        plan&apos;s default.
      </p>
    </Section>
  );
}

// Six states, not two — `fenced` is CORRECT (this node must not run it), `stray` is a
// placement failure, `unknown` is an absence of information. Collapsing them into
// running/stopped is exactly the bug CLAUDE.md records: a healthy app on another node
// reported as "not running, nothing is served".
const nodeTone = (s: string) =>
  s === "running" ? "ok" : s === "failed" || s === "stray" ? "bad" : s === "unknown" ? "warn" : "neutral";

const nodeColumns: DataListColumn<AppNodeView>[] = [
  { key: "node", label: "Node", sortable: true },
  {
    key: "state",
    label: "State",
    sortable: true,
    value: (n) => n.state,
    render: (n) => <StatusPill tone={nodeTone(n.state)}>{n.state}</StatusPill>,
  },
  { key: "candidate", label: "Candidate", value: (n) => (n.candidate ? "yes" : "no"), render: (n) => (n.candidate ? "✓" : "—") },
  { key: "leaseholder", label: "Lease", value: (n) => (n.leaseholder ? "yes" : "no"), render: (n) => (n.leaseholder ? "✓" : "—") },
  { key: "uptime", label: "Uptime", value: (n) => n.uptime || "" , render: (n) => n.uptime || "—" },
  { key: "note", label: "Note", value: (n) => n.note || "", render: (n) => n.note || "—" },
];

export function AppDetailScreen() {
  const { name } = useParams<{ name: string }>();
  const api = useAccountsApi();
  const navigate = useNavigate();
  const runner = useActionRunner<Backend>();

  const app = useQuery({ queryKey: ["app", name], queryFn: () => api.getApp(name!), enabled: !!name });
  const actions = appActions(api, { onDeleted: () => navigate("/apps") });

  if (app.isLoading || app.isError || !app.data) {
    return (
      <div>
        <StateView loading={app.isLoading} error={app.error} errorContext="this app" />
      </div>
    );
  }

  const b = app.data;

  return (
    <div>
      <PageHeader
        crumbs={[{ label: "App backends", to: "/apps" }, { label: b.name }]}
        title={b.name}
        status={
          <>
            <StatusPill tone={b.enabled ? "ok" : "neutral"}>{b.enabled ? "enabled" : "disabled"}</StatusPill>
            {b.live && <StatusPill tone={b.live.healthy ? "ok" : "bad"}>{b.live.summary}</StatusPill>}
          </>
        }
        subtitle={b.domain}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Terminal leads the header actions for a container that is running somewhere. */}
            {b.runtime === "container" && (b.live?.nodes ?? []).some((n) => n.running) && <ContainerConsole name={b.name} />}
            <ActionBar actions={actions} obj={b} runner={runner} />
          </div>
        }
      />

      <DataDetail
        fields={[
          { label: "Tenant", value: b.tenant_name || "—" },
          { label: "Runtime", value: b.runtime },
          { label: "Image", value: b.image || "—" },
          { label: "Command", value: b.command || "—" },
          { label: "Working dir", value: b.working_dir || "—" },
          { label: "Port", value: b.port },
          {
            // WHERE the site sends a request — the container port on each node running it. "Port"
            // alone did not say that the hostname routes there (asked 2026-09-24 about pbx).
            label: "Routes to",
            value: b.domain
              ? `${b.domain} → ${b.runtime === "container" ? "container port" : "port"} ${b.port || "—"}` +
                ((b.live?.nodes ?? []).filter((n) => n.running).length > 0
                  ? " · " +
                    (b.live?.nodes ?? [])
                      .filter((n) => n.running)
                      .map((n) => (n.ip && b.port ? `${n.node} → ${n.ip}:${b.port}` : n.node))
                      .join(" · ")
                  : " · not running anywhere")
              : "no hostname",
          },
          {
            label: "Placement",
            value: `${b.ha_mode || "process"} — ${b.nodes?.join(", ") || "this node"}`,
          },
          { label: "Unit", value: b.unit },
          { label: "Memory limit", value: b.mem_max_mb ? `${b.mem_max_mb} MB` : "unset" },
          { label: "Scratch space", value: b.scratch_mb ? `${b.scratch_mb} MB` : "plan default" },
        ]}
      />

      {/* The pinned image version, what each node holds, and the Update action (image_pin.go). */}
      {b.runtime === "container" && (
        <div className="mt-8">
          <ImageVersion appName={b.name} onChanged={() => void app.refetch()} />
        </div>
      )}
      {/* Whether the app ANSWERS, per node (appserver/health.go) — running is not working. */}
      <div className="mt-8">
        <AppHealth appName={b.name} />
      </div>
      <ResourceLimits app={b} onSaved={() => void app.refetch()} />

      {b.live && (
        <section className="mt-8">
          <h3 className="eyebrow mb-2.5 text-accent2">
            Per-node status — {b.live.running}/{b.live.expected} running
            {b.live.unknown > 0 && `, ${b.live.unknown} unknown`}
          </h3>
          <DataList items={b.live.nodes} columns={nodeColumns} getRowKey={(n) => n.node} />
        </section>
      )}

      <AppMeasured name={b.name} />
      <AppLogs name={b.name} />

      <RelatedLinks
        links={[
          ...(b.tenant_id ? [{ label: "Tenant", to: `/tenants/${b.tenant_id}`, hint: b.tenant_name }] : []),
          { label: "All apps", to: "/apps" },
        ]}
      />
      {runner.dialog}
    </div>
  );
}

// What is TRUE RIGHT NOW, as opposed to what the record says.
//
// The stored row is cluster-replicated; the container's address, id and restart count are per-node
// facts, and Docker's default bridge hands out the same 172.17.0.2 on every node — so this section
// always names the node that answered. Without that, a reader takes one node's runtime state for
// the cluster's.
function AppMeasured({ name }: { name: string }) {
  const api = useAccountsApi();
  const qc = useQueryClient();
  const { notify } = useToast();
  const { client } = useAuth();

  const q = useQ({ queryKey: ["app-inspect", name], queryFn: () => api.inspectApp(name), retry: false });
  const placeNodes = useQ({
    queryKey: ["placement-nodes"],
    queryFn: () => client.apiFetch<{ nodes: string[]; this_node: string }>("/apps/placement/nodes"),
    retry: false,
  });
  const d = q.data;
  const st = d?.state ?? {};
  const rt = d?.routing ?? {};
  const pl = d?.placement ?? {};
  const res = d?.resources ?? {};

  const done = (title: string) => {
    notify({ kind: "success", title });
    void qc.invalidateQueries({ queryKey: ["app-inspect", name] });
    void qc.invalidateQueries({ queryKey: ["app", name] });
  };
  const fail = (e: unknown) => notify({ kind: "error", title: "That did not work", body: e instanceof Error ? e.message : String(e) });

  return (
    <Section className="mt-8" title={`Measured now${d?.measured_on ? ` — on ${d.measured_on}` : ""}`}>
      <StateView loading={q.isLoading} error={q.error} errorContext="this app's live state">
        <div className="grid gap-x-8 gap-y-1.5 text-[13px] sm:grid-cols-2">
          <KV label="Container" value={st.running ? `running${st.id ? ` (${String(st.id).slice(0, 12)})` : ""}` : "not running here"} />
          {/* systemd's NRestarts is the ONLY signal that distinguishes a crash-looping container
              from a healthy one: a container restarting every second reads "active" at every
              instant it is looked at. */}
          <KV label="Restarts" value={st.restart_count != null ? String(st.restart_count) : "—"}
            warn={(st.restart_count ?? 0) > 0} />
          <KV label="Address" value={st.ip || d?.network?.ip || "—"} />
          <KV label="CPU" value={formatPercent(res.cpu_pct)} />
          <KV label="Memory" value={res.memory ? `${res.memory}${res.memory_pct != null ? ` (${res.memory_pct}%)` : ""}` : "—"} />
          <KV label="Disk written" value={res.size_rw_mb != null ? `${res.size_rw_mb} MB` : "—"} />
          <KV label="Image" value={d?.image || "—"} />
          <KV label="Placement" value={`${pl.kind || pl.ha_mode || "—"}${pl.nodes?.length ? ` — ${pl.nodes.join(", ")}` : ""}${pl.enforced === false ? " (not enforced)" : ""}`} />
        </div>

        {/* The route is resolved per node from this node's own observation and re-rendered each pass,
            so a mismatch is transient — the recorded address is momentarily behind the live container
            and the next reconcile follows it. Shown as information, not an alarm; Repoint just refreshes
            it now instead of waiting for the pass. */}
        {rt.drift && (
          <div className="mt-4 rounded-lg border border-line bg-surface2 p-3">
            <div className="text-[13px] font-medium">Route catching up to the container</div>
            <div className="mt-1 text-[12px] leading-relaxed text-muted">
              {rt.drift_detail || `This node last recorded ${rt.caddy_upstream || "?"}; the container is now at ${rt.live_upstream || "?"}. The next reconcile pass re-renders the route to it.`}
            </div>
            <Button size="sm" className="mt-2" variant="ghost" onClick={() => api.relinkApp(name).then(() => done("Route refreshed")).catch(fail)}>
              Refresh now
            </Button>
          </div>
        )}

        {(d?.volumes?.length ?? 0) > 0 && (
          <div className="mt-4">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Storage</div>
            <ul className="space-y-0.5 text-[12px]">
              {(d?.volumes ?? []).map((v, i) => (
                <li key={i} className="font-mono">
                  {v.path || v.source}
                  {v.backing && <span className="ml-2 font-sans text-muted">{v.backing}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          {!rt.drift && (
            <Button size="sm" variant="ghost" onClick={() => api.relinkApp(name).then(() => done("Routing repointed")).catch(fail)}>
              Repoint routing
            </Button>
          )}
        </div>

        {/* Which nodes it may run on — a checklist + mode, shared with the account console
            (PlacementPicker in @ozpanel/kit). POST /apps/{name}/placement validates the names and
            applies start/fence here. */}
        <div className="mt-4 max-w-md">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Where it runs</div>
          <PlacementPicker
            nodes={placeNodes.data?.nodes ?? []}
            thisNode={placeNodes.data?.this_node}
            value={{ ha_mode: pl.ha_mode || "singleton", nodes: (pl.nodes ?? []) as string[] }}
            applyLabel="Apply placement"
            onApply={(nextP) =>
              api
                .setAppPlacement(name, nextP.nodes, nextP.ha_mode)
                .then((r) => done(r?.note || (r?.running_here ? "Moved — running on this node" : "Placement applied")))
                .catch(fail)
            }
          />
        </div>
        {/* Manual "run it here now" for an interlocked app — moves the placement lease to a chosen
            candidate without changing the set, so it stays interlocked and still fails over. */}
        {(pl.kind === "interlocked" || (pl.ha_mode === "singleton" && ((pl.nodes ?? []) as string[]).length > 1)) && (
          <div className="mt-4 max-w-md">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Run it on a node now</div>
            <div className="flex flex-wrap gap-2">
              {((pl.nodes ?? []) as string[]).map((nd) => (
                <Button
                  key={nd}
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    client
                      .apiFetch<{ note?: string }>(`/apps/${name}/run-on`, { method: "POST", body: JSON.stringify({ node: nd }) })
                      .then((r) => done(r?.note || `Moving to ${nd}`))
                      .catch(fail)
                  }
                >
                  Run on {nd}
                </Button>
              ))}
            </div>
            <p className="mt-1 text-[11.5px] leading-relaxed text-muted">
              Moves the live instance to that node now, keeping it interlocked (it still fails over). ~30s; route and DNS follow.
            </p>
          </div>
        )}
      </StateView>

    </Section>
  );
}

function KV({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1">
      <span className="text-muted">{label}</span>
      <span className={`text-right font-mono text-[12.5px] ${warn ? "text-warn" : ""}`}>{value}</span>
    </div>
  );
}

function AppLogs({ name }: { name: string }) {
  const api = useAccountsApi();
  const [lines, setLines] = React.useState(200);
  const q = useQ({ queryKey: ["app-logs", name, lines], queryFn: () => api.appLogs(name, lines), retry: false });

  return (
    <Section
      className="mt-6"
      title="Logs"
      actions={
        <div className="flex items-center gap-2">
          <select value={lines} onChange={(e) => setLines(Number(e.target.value))}
            className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg">
            {[50, 200, 1000].map((n) => <option key={n} value={n}>{n} lines</option>)}
          </select>
          <Button size="sm" variant="ghost" onClick={() => void q.refetch()}>Refresh</Button>
        </div>
      }
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="this app's logs">
        {/* Which node answered. Logs are per-node and an app can be running elsewhere, so a quiet
            log here is not evidence the app is quiet. */}
        {q.data?.from_node && (
          <div className="mb-2 text-[11.5px] text-muted">
            From {q.data.from_node}
            {(q.data.nodes?.length ?? 0) > 1 && ` · also runs on ${(q.data.nodes ?? []).filter((n) => n.node !== q.data?.from_node).map((n) => n.node).join(", ")}`}
          </div>
        )}
        {/* The unit's journal: the app's own output as it is, and the panel's start gates
            (app-ready, app-gate — why it is HELD or REFUSED) laid out as the one log line. */}
        <LogLines rows={q.data?.logs} heightClass="max-h-96" empty="Nothing logged." />
      </StateView>
    </Section>
  );
}

/** A shell inside the app's container, on whichever node runs it (POST /terminal/app-sessions — the
 *  same route the account console uses; the server resolves the name to the container). */
function ContainerConsole({ name }: { name: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [busy, setBusy] = React.useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const s = await client.apiFetch<{ path?: string }>("/terminal/app-sessions", {
        method: "POST",
        body: JSON.stringify({ app: name, ttl_seconds: 3600 }),
      });
      if (s?.path) window.open(s.path, "_blank", "noopener");
      else notify({ kind: "error", title: "Could not open a console", body: "The session returned no address." });
    } catch (e) {
      notify({ kind: "error", title: "Could not open a console", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" disabled={busy} onClick={() => void open()} title="A shell inside the running container, in a new tab">
      {busy ? "Opening…" : "Terminal"}
    </Button>
  );
}
